// BOORO: the team of a space. Who works on what, and how the agents hang
// together. Pure logic, safe to import from web, desktop and mobile.

/** The state an agent shows in the team view. */
export type AgentLight = "busy" | "waiting" | "problem" | "stuck" | "idle";

/** How long an agent may be quiet before it counts as stuck. */
export const STUCK_MINUTES = 240;

/** The work states of a run. */
const BUSY_STATUSES = new Set(["queued", "leased", "running"]);
const WAITING_STATUSES = new Set(["waiting_input", "waiting_takeover"]);

/**
 * The state of an agent, as the team view shows it. An agent that works is
 * busy, one that asks a question waits, one that failed has a problem, and one
 * that has been quiet for hours is stuck.
 */
export function agentLight(input: {
  status: string | null | undefined;
  runErrorId: string | null | undefined;
  /** When the work last moved. */
  progressAt: string | null | undefined;
  now: Date;
}): AgentLight {
  const status = input.status ?? "";
  if (BUSY_STATUSES.has(status)) return "busy";
  if (WAITING_STATUSES.has(status)) return "waiting";
  if (input.runErrorId) return "problem";
  if (input.progressAt) {
    const age = input.now.getTime() - new Date(input.progressAt).getTime();
    if (age > STUCK_MINUTES * 60 * 1000) return "stuck";
  }
  return "idle";
}

/** One agent in the tree of the team. */
export type AgentTreeRow = {
  id: string;
  parentId: string | null;
  /** How deep the agent sits in the tree; the roots are at 0. */
  depth: number;
  hasChildren: boolean;
};

/**
 * The agents of a space as a tree. An agent whose parent is not in the list
 * becomes a root, and a cycle never hides an agent.
 */
export function buildAgentTree(
  rows: readonly { id: string; parentBotId: string | null }[],
): AgentTreeRow[] {
  type Row = { id: string; parentBotId: string | null };
  const byId = new Map(rows.map((row) => [row.id, row]));
  const children = new Map<string, Row[]>();
  const roots: Row[] = [];
  for (const row of rows) {
    const parentId = row.parentBotId;
    if (parentId && byId.has(parentId) && parentId !== row.id) {
      const siblings = children.get(parentId) ?? [];
      siblings.push(row);
      children.set(parentId, siblings);
    } else {
      roots.push(row);
    }
  }

  const out: AgentTreeRow[] = [];
  const visited = new Set<string>();
  const visit = (row: Row, depth: number, parentId: string | null) => {
    if (visited.has(row.id)) return;
    visited.add(row.id);
    const kids = children.get(row.id) ?? [];
    out.push({ id: row.id, parentId, depth, hasChildren: kids.length > 0 });
    for (const child of kids) visit(child, depth + 1, row.id);
  };

  for (const root of roots) visit(root, 0, null);
  // A cycle has no root; the agents that are left stay visible.
  for (const row of rows) {
    if (!visited.has(row.id)) visit(row, 0, null);
  }
  return out;
}

/**
 * Whether a group may move under another one. Both must sit in the same
 * office, and no group may sit under itself or one of its own subgroups.
 */
export function canMoveGroup(
  groups: readonly { id: string; spaceId: string; parentGroupId: string | null }[],
  groupId: string,
  parentGroupId: string | null,
): boolean {
  const byId = new Map(groups.map((group) => [group.id, group]));
  const group = byId.get(groupId);
  if (!group) return false;
  if (parentGroupId === null) return true;
  const parent = byId.get(parentGroupId);
  if (!parent || parent.spaceId !== group.spaceId) return false;
  // Walk up from the new parent: if the group shows up, it would be its own ancestor.
  const seen = new Set<string>();
  let current: string | null = parentGroupId;
  while (current) {
    if (current === groupId || seen.has(current)) return false;
    seen.add(current);
    current = byId.get(current)?.parentGroupId ?? null;
  }
  return true;
}

/** The group and all groups under it. */
export function groupWithDescendants(
  groups: readonly { id: string; parentGroupId: string | null }[],
  groupId: string,
): Set<string> {
  const out = new Set([groupId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const group of groups) {
      if (group.parentGroupId && out.has(group.parentGroupId) && !out.has(group.id)) {
        out.add(group.id);
        grew = true;
      }
    }
  }
  return out;
}

/** What a view is narrowed to: an office, a group with everything under it, or one agent. */
export type OrgScope = { spaceId: string | null; groupId: string | null; botId: string | null };

export const EMPTY_SCOPE: OrgScope = { spaceId: null, groupId: null, botId: null };

type ScopeGroup = { id: string; spaceId: string; parentGroupId: string | null };
type ScopeAgent = { id: string; spaceId: string; groupId: string | null };

/** Whether an agent lies in the scope. With no scope, every agent does. */
export function agentInScope(
  groups: readonly ScopeGroup[],
  agent: ScopeAgent,
  scope: OrgScope,
): boolean {
  if (scope.botId) return agent.id === scope.botId;
  if (scope.groupId) {
    return agent.groupId !== null && groupWithDescendants(groups, scope.groupId).has(agent.groupId);
  }
  if (scope.spaceId) return agent.spaceId === scope.spaceId;
  return true;
}

/**
 * Whether something of an office that belongs to an agent lies in the scope,
 * such as a task. Without an agent it only counts when the scope is an office.
 */
export function itemInScope(
  groups: readonly ScopeGroup[],
  agents: readonly ScopeAgent[],
  item: { spaceId: string; botId: string | null },
  scope: OrgScope,
): boolean {
  if (scope.spaceId && item.spaceId !== scope.spaceId) return false;
  if (!scope.botId && !scope.groupId) return true;
  const agent = agents.find((candidate) => candidate.id === item.botId);
  return agent ? agentInScope(groups, agent, scope) : false;
}

/** The groups of an office in tree order, each with its depth, for a picker. */
export function orderedGroups<
  Group extends { id: string; spaceId: string; parentGroupId: string | null; position: number },
>(groups: readonly Group[], spaceId: string | null): { group: Group; depth: number }[] {
  const inOffice = groups.filter((group) => spaceId === null || group.spaceId === spaceId);
  const ids = new Set(inOffice.map((group) => group.id));
  const children = new Map<string | null, Group[]>();
  for (const group of inOffice) {
    // A group whose parent is gone sits at the top, so it never disappears.
    const parent = group.parentGroupId && ids.has(group.parentGroupId) ? group.parentGroupId : null;
    const list = children.get(parent) ?? [];
    list.push(group);
    children.set(parent, list);
  }
  const out: { group: Group; depth: number }[] = [];
  const seen = new Set<string>();
  const visit = (parent: string | null, depth: number) => {
    const list = [...(children.get(parent) ?? [])].sort((a, b) => a.position - b.position);
    for (const group of list) {
      if (seen.has(group.id)) continue;
      seen.add(group.id);
      out.push({ group, depth });
      visit(group.id, depth + 1);
    }
  };
  visit(null, 0);
  return out;
}

/** The group at the top of the office that holds a group, or null outside any group. */
export function topGroupOf<Group extends { id: string; parentGroupId: string | null }>(
  groups: readonly Group[],
  groupId: string | null,
): Group | null {
  const byId = new Map(groups.map((group) => [group.id, group]));
  const seen = new Set<string>();
  let current = groupId ? byId.get(groupId) : undefined;
  while (current?.parentGroupId && !seen.has(current.id)) {
    seen.add(current.id);
    const parent = byId.get(current.parentGroupId);
    if (!parent) break;
    current = parent;
  }
  return current ?? null;
}

export type { LayoutNode } from "./layout.js";
export {
  AGENT_HEIGHT,
  AGENT_WIDTH,
  agentNodeId,
  CHIEF_HEIGHT,
  CHIEF_WIDTH,
  CHILD_GAP,
  groupNodeId,
  HEADER,
  LEAD_GAP,
  layoutOrg,
  officeNodeId,
  PADDING,
} from "./layout.js";
